import { ILocationResponse } from '@kcftech/prometheus-client';
import { DocumentTitle } from 'components/DocumentTitle';
import { DetailsPage } from 'components/page/DetailsPage';
import { useFeatureFlag } from 'configcat-react';
import { usePermissions } from 'contexts/PermissionsContext';
import { useNodeSettingsOptions } from 'custom_hooks/data_hooks/nodes/useNodeSettingsOptions';
import { useV4SensorSettings } from 'custom_hooks/data_hooks/v4_sensor_settings/useV4SensorSettings';
import { AdvancedSettingsSection } from 'pages/Settings/V4SensorSettings/AdvancedSettingsSection';
import { CollectionSettingsSection } from 'pages/Settings/V4SensorSettings/CollectionSettingsSection';
import { V4SensorSettingsEditor } from 'pages/Settings/V4SensorSettings/V4SensorSettingsEditor';
import {
    ActionRow,
    Callout,
    PageContent,
    PageHeader,
    PageTitle,
    SettingsCard,
} from 'pages/Settings/V4SensorSettings/styled';
import { useState } from 'react';
import { StyledButton } from 'shared/button/Button';

type Props = Readonly<{
    location: ILocationResponse;
}>;

export function V4SensorSettingsPage({ location }: Props) {
    const { value: v4SensorsEnabled } = useFeatureFlag('v4Sensors', false);
    const { canEditV4SensorSettings } = usePermissions();
    const [isEditing, setIsEditing] = useState(false);
    const {
        data: settings,
        isLoading: isLoadingSettings,
        error: settingsError,
    } = useV4SensorSettings({
        locationId: location.Id,
    });
    // Read UpdateInterval directly: this page is location-scoped so there's no node
    // to feed useUpdateIntervalOptions; V4 in the modal hits the same unfiltered list.
    const {
        data: nodeSettingsOptions,
        isLoading: isLoadingOptions,
        error: optionsError,
    } = useNodeSettingsOptions({
        locationId: location.Id,
    });
    const updateIntervalOptions = nodeSettingsOptions?.UpdateInterval ?? [];

    if (!v4SensorsEnabled) {
        return null;
    }

    return (
        <DetailsPage
            isLoading={isLoadingSettings || isLoadingOptions}
            error={
                settingsError || optionsError
                    ? 'Unable to load V4 sensor settings.'
                    : undefined
            }
            fullHeight
            fullWidth
            noPageScroll
        >
            {settings && (
                <>
                    <DocumentTitle title={`${location.Name} V4 Sensor Settings`} />
                    <PageContent>
                        <PageHeader>
                            <PageTitle>[{location.Name}] V4 Sensor Settings</PageTitle>
                            <Callout>
                                <span>
                                    Changes apply to V4 Sensors at [{location.Name}] but
                                    will not override individually configured V4 sensors
                                </span>
                            </Callout>
                        </PageHeader>
                        {isEditing ? (
                            <V4SensorSettingsEditor
                                location={location}
                                settings={settings}
                                updateIntervalOptions={updateIntervalOptions}
                                onDone={() => setIsEditing(false)}
                            />
                        ) : (
                            <>
                                {canEditV4SensorSettings && (
                                    <ActionRow>
                                        <StyledButton
                                            type="button"
                                            appearance="secondary"
                                            spacing="compact"
                                            onClick={() => setIsEditing(true)}
                                        >
                                            Edit
                                        </StyledButton>
                                    </ActionRow>
                                )}
                                <SettingsCard>
                                    <CollectionSettingsSection
                                        settings={settings}
                                        updateIntervalOptions={updateIntervalOptions}
                                    />
                                    <AdvancedSettingsSection settings={settings} />
                                </SettingsCard>
                            </>
                        )}
                    </PageContent>
                </>
            )}
        </DetailsPage>
    );
}
